import React, { useEffect, useState } from "react";

import { Button } from "../../../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
  DialogDescription,
} from "../../../design/dialog";
import { Input } from "../../../design/input";
import { Field, Fieldset, FieldGroup, Label, Description } from "../../../design/fieldset";
import { useCase } from "../../../contexts/CaseContext";
import { Case, CaseStatus, ProgramType, PROGRAM_TYPE_LABELS } from "@visitproof/types";
import { Textarea } from "../../../design/textarea";
import { Select } from "../../../design/select";
import { Switch, SwitchField } from "../../../design/switch";
import { Text } from "../../../design/text";
import { formatDateUTC, parseDateToTimestamp } from "../../../utils/date";
import {
  ReasonForSupervision,
  REASON_LABELS,
  ServiceType,
  SERVICE_TYPE_LABELS,
  PRIMARY_SERVICE_TYPES,
  ServiceClassification,
  SERVICE_CLASSIFICATION_LABELS,
  CLASSIFIABLE_SERVICE_TYPES,
  ReferralSource,
  REFERRAL_SOURCE_LABELS,
  ExParteStatus,
  EX_PARTE_STATUS_LABELS,
  VictimizationType,
  VICTIMIZATION_TYPE_LABELS,
  PresentingConcern,
  PRESENTING_CONCERN_LABELS,
} from "@visitproof/types";
import {
  FolderIcon,
  ScaleIcon,
  ShieldExclamationIcon,
  HeartIcon,
  ClipboardDocumentListIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";

interface Props {
  caseItem: Case;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

// Section Header Component
const SectionHeader: React.FC<{
  icon: React.ElementType;
  title: string;
  description?: string;
}> = ({ icon: Icon, title, description }) => (
  <div className="flex items-start gap-3 mb-4">
    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-zinc-100 dark:bg-zinc-800">
      <Icon className="h-5 w-5 text-zinc-600 dark:text-zinc-400" />
    </div>
    <div>
      <Text className="font-semibold text-zinc-900 dark:text-zinc-100">{title}</Text>
      {description && (
        <Text className="text-sm text-zinc-500 dark:text-zinc-400">{description}</Text>
      )}
    </div>
  </div>
);

// Section Container Component
const Section: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className = "" }) => (
  <div className={`rounded-lg border border-zinc-200 bg-zinc-50/50 p-4 dark:border-zinc-700 dark:bg-zinc-800/50 ${className}`}>
    {children}
  </div>
);

const EditCaseDialog: React.FC<Props> = ({ caseItem, isOpen, setIsOpen }) => {
  const { updateCase } = useCase();
  const [identifier, setIdentifier] = useState<string>(
    caseItem.identifier ?? "",
  );
  const [status, setStatus] = useState<CaseStatus>(
    caseItem.status ?? "open",
  );
  const [closedDate, setClosedDate] = useState<string>(
    formatDateUTC(caseItem.closedAt, "input")
  );
  const [closureNotes, setClosureNotes] = useState<string>(caseItem.closureNotes ?? "");
  const [notes, setNotes] = useState<string>(caseItem.notes ?? "");
  const [isUploading, setIsUploading] = useState(false);

  // Program types (array for multiple selection)
  const [programTypes, setProgramTypes] = useState<ProgramType[]>(
    caseItem.programTypes ?? (caseItem.programType ? [caseItem.programType] : [])
  );

  // Parent confirmation override
  const [parentConfirmation, setParentConfirmation] = useState<'require' | 'skip' | 'default'>(
    caseItem.parentConfirmation ?? 'default'
  );

  // Demographics state
  const [reasonForSupervision, setReasonForSupervision] = useState<ReasonForSupervision | "">(
    caseItem.reasonForSupervision ?? ""
  );
  const [reasonForSupervisionOther, setReasonForSupervisionOther] = useState<string>(
    caseItem.reasonForSupervisionOther ?? ""
  );
  const [presentingConcerns, setPresentingConcerns] = useState<PresentingConcern[]>(
    caseItem.presentingConcerns ?? []
  );
  const [presentingConcernsOther, setPresentingConcernsOther] = useState<string>(
    caseItem.presentingConcernsOther ?? ""
  );
  const [referralSourceType, setReferralSourceType] = useState<ReferralSource | "">(
    caseItem.referralSourceType ?? ""
  );
  const [referralSourceOther, setReferralSourceOther] = useState<string>(
    caseItem.referralSourceOther ?? ""
  );
  const [referralDate, setReferralDate] = useState<string>(
    formatDateUTC(caseItem.referralDate, "input")
  );

  // Service types (array for multiple selection)
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>(
    caseItem.serviceTypes ?? (caseItem.serviceType ? [caseItem.serviceType] : [])
  );
  // Service classifications (DV, Sexual Assault, DSS Child Welfare)
  const [serviceClassifications, setServiceClassifications] = useState<ServiceClassification[]>(
    caseItem.serviceClassifications ?? []
  );
  const [intakeDate, setIntakeDate] = useState<string>(
    formatDateUTC(caseItem.intakeDate, "input")
  );

  // Legal/Court fields
  const [isCourtOrdered, setIsCourtOrdered] = useState<boolean>(
    caseItem.isCourtOrdered ?? false
  );
  // Universal order types
  const [hasDVO, setHasDVO] = useState<boolean>(
    caseItem.hasDVO ?? caseItem.hasDV50B ?? false
  );
  const [dvoExpirationDate, setDvoExpirationDate] = useState<string>(
    formatDateUTC(caseItem.dvoExpirationDate, "input")
  );
  const [hasTRO, setHasTRO] = useState<boolean>(
    caseItem.hasTRO ?? false
  );
  const [troExpirationDate, setTroExpirationDate] = useState<string>(
    formatDateUTC(caseItem.troExpirationDate, "input")
  );
  const [hasRO, setHasRO] = useState<boolean>(
    caseItem.hasRO ?? caseItem.has50BProtectionOrder ?? false
  );
  const [roExpirationDate, setRoExpirationDate] = useState<string>(
    formatDateUTC(caseItem.roExpirationDate, "input") || formatDateUTC(caseItem.protectionOrder50BDate, "input")
  );
  const [hasTempOrders, setHasTempOrders] = useState<boolean>(
    caseItem.hasTempOrders ?? caseItem.has50COrder ?? false
  );
  const [tempOrdersExpirationDate, setTempOrdersExpirationDate] = useState<string>(
    formatDateUTC(caseItem.tempOrdersExpirationDate, "input") || formatDateUTC(caseItem.order50CDate, "input")
  );
  const [exParteStatus, setExParteStatus] = useState<ExParteStatus | "">(
    caseItem.exParteStatus ?? ""
  );
  const [judgeName, setJudgeName] = useState<string>(
    caseItem.judgeName ?? ""
  );

  // Safety information
  const [safetyInformation, setSafetyInformation] = useState<string>(
    caseItem.safetyInformation ?? ""
  );

  // Victimization
  const [primaryVictimizationType, setPrimaryVictimizationType] = useState<VictimizationType | "">(
    caseItem.primaryVictimizationType ?? ""
  );
  const [primaryVictimizationOther, setPrimaryVictimizationOther] = useState<string>(
    caseItem.primaryVictimizationOther ?? ""
  );
  const [offenderName, setOffenderName] = useState<string>(
    caseItem.offenderName ?? ""
  );

  // Counseling
  const [inCounseling, setInCounseling] = useState<boolean>(
    caseItem.inCounseling ?? false
  );
  const [courtOrderedCounseling, setCourtOrderedCounseling] = useState<boolean>(
    caseItem.courtOrderedCounseling ?? false
  );
  const [interestedInTherapeuticVisits, setInterestedInTherapeuticVisits] = useState<boolean>(
    caseItem.interestedInTherapeuticVisits ?? false
  );

  // Reset form fields when `caseItem` changes or when the dialog opens
  useEffect(() => {
    if (isOpen) {
      setIdentifier(caseItem.identifier ?? "");
      setStatus(caseItem.status ?? "open");
      setClosedDate(formatDateUTC(caseItem.closedAt, "input"));
      setClosureNotes(caseItem.closureNotes ?? "");
      setNotes(caseItem.notes ?? "");
      // Program types
      setProgramTypes(caseItem.programTypes ?? (caseItem.programType ? [caseItem.programType] : []));
      // Parent confirmation
      setParentConfirmation(caseItem.parentConfirmation ?? 'default');
      // Demographics
      setReasonForSupervision(caseItem.reasonForSupervision ?? "");
      setReasonForSupervisionOther(caseItem.reasonForSupervisionOther ?? "");
      setPresentingConcerns(caseItem.presentingConcerns ?? []);
      setPresentingConcernsOther(caseItem.presentingConcernsOther ?? "");
      setReferralSourceType(caseItem.referralSourceType ?? "");
      setReferralSourceOther(caseItem.referralSourceOther ?? "");
      setReferralDate(formatDateUTC(caseItem.referralDate, "input"));
      // Service types and classifications
      setServiceTypes(caseItem.serviceTypes ?? (caseItem.serviceType ? [caseItem.serviceType] : []));
      setServiceClassifications(caseItem.serviceClassifications ?? []);
      setIntakeDate(formatDateUTC(caseItem.intakeDate, "input"));
      // Legal/Court
      setIsCourtOrdered(caseItem.isCourtOrdered ?? false);
      // Universal order types (with fallback to deprecated fields)
      setHasDVO(caseItem.hasDVO ?? caseItem.hasDV50B ?? false);
      setDvoExpirationDate(formatDateUTC(caseItem.dvoExpirationDate, "input"));
      setHasTRO(caseItem.hasTRO ?? false);
      setTroExpirationDate(formatDateUTC(caseItem.troExpirationDate, "input"));
      setHasRO(caseItem.hasRO ?? caseItem.has50BProtectionOrder ?? false);
      setRoExpirationDate(
        formatDateUTC(caseItem.roExpirationDate, "input") || formatDateUTC(caseItem.protectionOrder50BDate, "input")
      );
      setHasTempOrders(caseItem.hasTempOrders ?? caseItem.has50COrder ?? false);
      setTempOrdersExpirationDate(
        formatDateUTC(caseItem.tempOrdersExpirationDate, "input") || formatDateUTC(caseItem.order50CDate, "input")
      );
      setExParteStatus(caseItem.exParteStatus ?? "");
      setJudgeName(caseItem.judgeName ?? "");
      // Safety
      setSafetyInformation(caseItem.safetyInformation ?? "");
      // Victimization
      setPrimaryVictimizationType(caseItem.primaryVictimizationType ?? "");
      setPrimaryVictimizationOther(caseItem.primaryVictimizationOther ?? "");
      setOffenderName(caseItem.offenderName ?? "");
      // Counseling
      setInCounseling(caseItem.inCounseling ?? false);
      setCourtOrderedCounseling(caseItem.courtOrderedCounseling ?? false);
      setInterestedInTherapeuticVisits(caseItem.interestedInTherapeuticVisits ?? false);
    }
  }, [caseItem, isOpen]);

  const handleSubmit = async () => {
    if (!caseItem.id) {
      console.error("Case ID is required");
      return;
    }
    setIsUploading(true);

    try {
      await updateCase(caseItem.id, {
        identifier,
        status,
        closedAt: status === "closed" && closedDate ? parseDateToTimestamp(closedDate) : undefined,
        closureNotes: status === "closed" ? closureNotes || undefined : undefined,
        notes,
        // Program types
        programTypes: programTypes.length > 0 ? programTypes : undefined,
        // Parent confirmation
        parentConfirmation: parentConfirmation !== 'default' ? parentConfirmation : undefined,
        // Demographics
        reasonForSupervision: reasonForSupervision || undefined,
        reasonForSupervisionOther: reasonForSupervision === "other" ? reasonForSupervisionOther : undefined,
        presentingConcerns: presentingConcerns.length > 0 ? presentingConcerns : undefined,
        presentingConcernsOther: presentingConcerns.includes("other") ? presentingConcernsOther : undefined,
        referralSourceType: referralSourceType || undefined,
        referralSourceOther: referralSourceType === "other" ? referralSourceOther : undefined,
        referralDate: referralDate ? parseDateToTimestamp(referralDate) : undefined,
        // Service types and classifications
        serviceTypes: serviceTypes.length > 0 ? serviceTypes : undefined,
        serviceClassifications: serviceClassifications.length > 0 ? serviceClassifications : undefined,
        intakeDate: intakeDate ? parseDateToTimestamp(intakeDate) : undefined,
        // Legal/Court
        isCourtOrdered,
        // Universal order types
        hasDVO,
        dvoExpirationDate: dvoExpirationDate ? parseDateToTimestamp(dvoExpirationDate) : undefined,
        hasTRO,
        troExpirationDate: troExpirationDate ? parseDateToTimestamp(troExpirationDate) : undefined,
        hasRO,
        roExpirationDate: roExpirationDate ? parseDateToTimestamp(roExpirationDate) : undefined,
        hasTempOrders,
        tempOrdersExpirationDate: tempOrdersExpirationDate ? parseDateToTimestamp(tempOrdersExpirationDate) : undefined,
        exParteStatus: exParteStatus || undefined,
        judgeName: judgeName || undefined,
        // Safety
        safetyInformation: safetyInformation || undefined,
        // Victimization
        primaryVictimizationType: primaryVictimizationType || undefined,
        primaryVictimizationOther: primaryVictimizationType === "other" ? primaryVictimizationOther : undefined,
        offenderName: offenderName || undefined,
        // Counseling
        inCounseling,
        courtOrderedCounseling,
        interestedInTherapeuticVisits,
      });
      clearForm();
    } catch (error) {
      alert("Failed to update case. Please try again later.");
    }

    setIsUploading(false);
  };

  const clearForm = () => {
    setIsOpen(false);
  };

  // Status options with visual selection
  const statusOptions = [
    {
      value: "open" as CaseStatus,
      label: "Open",
      icon: CheckCircleIcon,
      color: "text-green-600 dark:text-green-400",
      bgColor: "bg-green-50 dark:bg-green-900/20",
      borderColor: "border-green-500",
    },
    {
      value: "closed" as CaseStatus,
      label: "Closed",
      icon: XCircleIcon,
      color: "text-zinc-600 dark:text-zinc-400",
      bgColor: "bg-zinc-100 dark:bg-zinc-800",
      borderColor: "border-zinc-500",
    },
  ];

  return (
    <Dialog open={isOpen} onClose={() => setIsOpen(false)} size="2xl">
      <DialogTitle>Edit Case</DialogTitle>
      <DialogDescription>
        Update case information, demographics, and legal status.
      </DialogDescription>
      <DialogBody className="space-y-6">
        {/* Basic Information Section */}
        <Section>
          <SectionHeader
            icon={FolderIcon}
            title="Basic Information"
            description="Case identifier and status"
          />
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Case Identifier</Label>
                <Input
                  type="text"
                  value={identifier}
                  placeholder="e.g., CASE-2024-001"
                  onChange={(e) => setIdentifier(e.target.value)}
                />
              </Field>
              <div>
                <Text className="mb-3 block text-sm font-medium text-zinc-900 dark:text-zinc-100">
                  Program Types
                </Text>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {Object.entries(PROGRAM_TYPE_LABELS).map(([value, label]) => {
                    const isChecked = programTypes.includes(value as ProgramType);
                    return (
                      <label
                        key={value}
                        className={`flex cursor-pointer items-center gap-2 rounded-lg border p-2.5 text-sm transition-all ${
                          isChecked
                            ? "border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-900/20"
                            : "border-zinc-200 bg-white hover:border-zinc-300 dark:border-zinc-700 dark:bg-zinc-900 dark:hover:border-zinc-600"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setProgramTypes([...programTypes, value as ProgramType]);
                            } else {
                              setProgramTypes(programTypes.filter((t) => t !== value));
                            }
                          }}
                          className="h-4 w-4 rounded border-zinc-300 text-blue-600 focus:ring-blue-500 dark:border-zinc-600 dark:bg-zinc-800"
                        />
                        <span
                          className={`${
                            isChecked
                              ? "text-blue-700 dark:text-blue-300"
                              : "text-zinc-700 dark:text-zinc-300"
                          }`}
                        >
                          {label}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <Field>
                <Label>Notes</Label>
                <Input
                  type="text"
                  value={notes}
                  placeholder="Brief notes about the case"
                  onChange={(e) => setNotes(e.target.value)}
                />
              </Field>
              <Field>
                <Label>Parent Visit Confirmation</Label>
                <Description className="text-xs text-zinc-500 dark:text-zinc-400 mb-2">
                  Override the account default for whether parents must confirm visit attendance.
                </Description>
                <Select
                  value={parentConfirmation}
                  onChange={(e) => setParentConfirmation(e.target.value as 'require' | 'skip' | 'default')}
                >
                  <option value="default">Use account default</option>
                  <option value="require">Require confirmation</option>
                  <option value="skip">Skip confirmation</option>
                </Select>
              </Field>

              {/* Visual Status Selection */}
              <div>
                <Text className="mb-3 block text-sm font-medium text-zinc-900 dark:text-zinc-100">
                  Case Status
                </Text>
                <div className="grid grid-cols-2 gap-3">
                  {statusOptions.map((option) => {
                    const isSelected = status === option.value;
                    const StatusIcon = option.icon;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setStatus(option.value)}
                        className={`flex items-center gap-3 rounded-lg border-2 p-3 text-left transition-all ${
                          isSelected
                            ? `${option.borderColor} ${option.bgColor}`
                            : "border-zinc-200 bg-white hover:border-zinc-300 dark:border-zinc-700 dark:bg-zinc-900 dark:hover:border-zinc-600"
                        }`}
                      >
                        <StatusIcon
                          className={`h-5 w-5 ${
                            isSelected ? option.color : "text-zinc-400"
                          }`}
                        />
                        <span
                          className={`text-sm font-medium ${
                            isSelected
                              ? "text-zinc-900 dark:text-zinc-100"
                              : "text-zinc-600 dark:text-zinc-400"
                          }`}
                        >
                          {option.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Closure Details - shown when status is closed */}
              {status === "closed" && (
                <>
                  <Field>
                    <Label>Closed Date</Label>
                    <Input
                      type="date"
                      value={closedDate}
                      onChange={(e) => setClosedDate(e.target.value)}
                    />
                  </Field>
                  <Field>
                    <Label>Closure Notes</Label>
                    <Description>
                      Document the reason for closing this case (e.g., visits no longer needed,
                      terminated due to incident, no contact from parties, client will return when able)
                    </Description>
                    <Textarea
                      rows={3}
                      value={closureNotes}
                      onChange={(e) => setClosureNotes(e.target.value)}
                      placeholder="Enter the reason for closing this case..."
                    />
                  </Field>
                </>
              )}
            </FieldGroup>
          </Fieldset>
        </Section>

        {/* Demographics Section */}
        <Section>
          <SectionHeader
            icon={ClipboardDocumentListIcon}
            title="Case Demographics"
            description="Used for grant reporting purposes"
          />
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Reason for Supervision</Label>
                <Select
                  value={reasonForSupervision}
                  onChange={(e) => setReasonForSupervision(e.target.value as ReasonForSupervision)}
                >
                  <option value="">Select a reason...</option>
                  {Object.entries(REASON_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </Field>
              {reasonForSupervision === "other" && (
                <Field>
                  <Label>Other Reason</Label>
                  <Input
                    type="text"
                    value={reasonForSupervisionOther}
                    placeholder="Specify the reason"
                    onChange={(e) => setReasonForSupervisionOther(e.target.value)}
                  />
                </Field>
              )}

              <div>
                <Text className="mb-3 block text-sm font-medium text-zinc-900 dark:text-zinc-100">
                  Presenting Concerns
                </Text>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {Object.entries(PRESENTING_CONCERN_LABELS).map(([value, label]) => {
                    const isChecked = presentingConcerns.includes(value as PresentingConcern);
                    return (
                      <label
                        key={value}
                        className={`flex cursor-pointer items-center gap-2 rounded-lg border p-2.5 text-sm transition-all ${
                          isChecked
                            ? "border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-900/20"
                            : "border-zinc-200 bg-white hover:border-zinc-300 dark:border-zinc-700 dark:bg-zinc-900 dark:hover:border-zinc-600"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setPresentingConcerns([...presentingConcerns, value as PresentingConcern]);
                            } else {
                              setPresentingConcerns(presentingConcerns.filter((c) => c !== value));
                            }
                          }}
                          className="h-4 w-4 rounded border-zinc-300 text-blue-600 focus:ring-blue-500 dark:border-zinc-600 dark:bg-zinc-800"
                        />
                        <span
                          className={
                            isChecked
                              ? "text-blue-700 dark:text-blue-300"
                              : "text-zinc-700 dark:text-zinc-300"
                          }
                        >
                          {label}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
              {presentingConcerns.includes("other") && (
                <Field>
                  <Label>Other Presenting Concern</Label>
                  <Input
                    type="text"
                    value={presentingConcernsOther}
                    placeholder="Specify the concern"
                    onChange={(e) => setPresentingConcernsOther(e.target.value)}
                  />
                </Field>
              )}

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field>
                  <Label>Referral Source</Label>
                  <Select
                    value={referralSourceType}
                    onChange={(e) => setReferralSourceType(e.target.value as ReferralSource)}
                  >
                    <option value="">Select source...</option>
                    {Object.entries(REFERRAL_SOURCE_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field>
                  <Label>Referral Date</Label>
                  <Input
                    type="date"
                    value={referralDate}
                    onChange={(e) => setReferralDate(e.target.value)}
                  />
                </Field>
              </div>
              {referralSourceType === "other" && (
                <Field>
                  <Label>Other Referral Source</Label>
                  <Input
                    type="text"
                    value={referralSourceOther}
                    placeholder="Specify the referral source"
                    onChange={(e) => setReferralSourceOther(e.target.value)}
                  />
                </Field>
              )}

              <div>
                <Text className="mb-3 block text-sm font-medium text-zinc-900 dark:text-zinc-100">
                  Service Types
                </Text>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  {PRIMARY_SERVICE_TYPES.map((value) => {
                    const isChecked = serviceTypes.includes(value);
                    const label = SERVICE_TYPE_LABELS[value];
                    return (
                      <label
                        key={value}
                        className={`flex cursor-pointer items-center gap-2 rounded-lg border p-2.5 text-sm transition-all ${
                          isChecked
                            ? "border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-900/20"
                            : "border-zinc-200 bg-white hover:border-zinc-300 dark:border-zinc-700 dark:bg-zinc-900 dark:hover:border-zinc-600"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setServiceTypes([...serviceTypes, value]);
                            } else {
                              setServiceTypes(serviceTypes.filter((t) => t !== value));
                            }
                          }}
                          className="h-4 w-4 rounded border-zinc-300 text-blue-600 focus:ring-blue-500 dark:border-zinc-600 dark:bg-zinc-800"
                        />
                        <span
                          className={`${
                            isChecked
                              ? "text-blue-700 dark:text-blue-300"
                              : "text-zinc-700 dark:text-zinc-300"
                          }`}
                        >
                          {label}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Service Classifications - shown when classifiable service types are selected */}
              {serviceTypes.some((st) => CLASSIFIABLE_SERVICE_TYPES.includes(st)) && (
                <div>
                  <Text className="mb-3 block text-sm font-medium text-zinc-900 dark:text-zinc-100">
                    Service Classifications
                  </Text>
                  <Description className="mb-2 text-sm text-zinc-500">
                    Applicable for Supervised Visitation and Safe Exchange services
                  </Description>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                    {Object.entries(SERVICE_CLASSIFICATION_LABELS).map(([value, label]) => {
                      const isChecked = serviceClassifications.includes(value as ServiceClassification);
                      return (
                        <label
                          key={value}
                          className={`flex cursor-pointer items-center gap-2 rounded-lg border p-2.5 text-sm transition-all ${
                            isChecked
                              ? "border-purple-500 bg-purple-50 dark:border-purple-400 dark:bg-purple-900/20"
                              : "border-zinc-200 bg-white hover:border-zinc-300 dark:border-zinc-700 dark:bg-zinc-900 dark:hover:border-zinc-600"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setServiceClassifications([...serviceClassifications, value as ServiceClassification]);
                              } else {
                                setServiceClassifications(serviceClassifications.filter((c) => c !== value));
                              }
                            }}
                            className="h-4 w-4 rounded border-zinc-300 text-purple-600 focus:ring-purple-500 dark:border-zinc-600 dark:bg-zinc-800"
                          />
                          <span
                            className={`${
                              isChecked
                                ? "text-purple-700 dark:text-purple-300"
                                : "text-zinc-700 dark:text-zinc-300"
                            }`}
                          >
                            {label}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              <Field>
                <Label>Intake Date</Label>
                <Input
                  type="date"
                  value={intakeDate}
                  onChange={(e) => setIntakeDate(e.target.value)}
                />
              </Field>
            </FieldGroup>
          </Fieldset>
        </Section>

        {/* Legal Status Section */}
        <Section>
          <SectionHeader
            icon={ScaleIcon}
            title="Legal Status"
            description="Court orders and legal information"
          />
          <Fieldset>
            <FieldGroup>
              {/* Court Ordered Toggle */}
              <div className="rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900">
                <SwitchField>
                  <Label>Court Ordered</Label>
                  <Switch
                    checked={isCourtOrdered}
                    onChange={() => setIsCourtOrdered(!isCourtOrdered)}
                  />
                </SwitchField>
              </div>

              {/* Order Types with Expiration Dates */}
              <Text className="mt-4 mb-2 text-sm font-medium text-zinc-700 dark:text-zinc-300">
                Protection Orders
              </Text>

              {/* DVO - Domestic Violence Order */}
              <div className="rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900 space-y-3">
                <SwitchField>
                  <Label>Domestic Violence Order (DVO)</Label>
                  <Switch
                    checked={hasDVO}
                    onChange={() => setHasDVO(!hasDVO)}
                  />
                </SwitchField>
                {hasDVO && (
                  <Field>
                    <Label>DVO Expiration Date</Label>
                    <Input
                      type="date"
                      value={dvoExpirationDate}
                      onChange={(e) => setDvoExpirationDate(e.target.value)}
                    />
                  </Field>
                )}
              </div>

              {/* TRO - Temporary Restraining Order */}
              <div className="rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900 space-y-3">
                <SwitchField>
                  <Label>Temporary Restraining Order (TRO)</Label>
                  <Switch
                    checked={hasTRO}
                    onChange={() => setHasTRO(!hasTRO)}
                  />
                </SwitchField>
                {hasTRO && (
                  <Field>
                    <Label>TRO Expiration Date</Label>
                    <Input
                      type="date"
                      value={troExpirationDate}
                      onChange={(e) => setTroExpirationDate(e.target.value)}
                    />
                  </Field>
                )}
              </div>

              {/* RO - Restraining Order */}
              <div className="rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900 space-y-3">
                <SwitchField>
                  <Label>Restraining Order (RO)</Label>
                  <Switch
                    checked={hasRO}
                    onChange={() => setHasRO(!hasRO)}
                  />
                </SwitchField>
                {hasRO && (
                  <Field>
                    <Label>RO Expiration Date</Label>
                    <Input
                      type="date"
                      value={roExpirationDate}
                      onChange={(e) => setRoExpirationDate(e.target.value)}
                    />
                  </Field>
                )}
              </div>

              {/* Temp Orders */}
              <div className="rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900 space-y-3">
                <SwitchField>
                  <Label>Temporary Orders</Label>
                  <Switch
                    checked={hasTempOrders}
                    onChange={() => setHasTempOrders(!hasTempOrders)}
                  />
                </SwitchField>
                {hasTempOrders && (
                  <Field>
                    <Label>Temp Orders Expiration Date</Label>
                    <Input
                      type="date"
                      value={tempOrdersExpirationDate}
                      onChange={(e) => setTempOrdersExpirationDate(e.target.value)}
                    />
                  </Field>
                )}
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 mt-4">
                <Field>
                  <Label>Ex Parte Status</Label>
                  <Select
                    value={exParteStatus}
                    onChange={(e) => setExParteStatus(e.target.value as ExParteStatus)}
                  >
                    <option value="">Select status...</option>
                    {Object.entries(EX_PARTE_STATUS_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field>
                  <Label>Judge Name</Label>
                  <Input
                    type="text"
                    value={judgeName}
                    placeholder="Presiding judge"
                    onChange={(e) => setJudgeName(e.target.value)}
                  />
                </Field>
              </div>
            </FieldGroup>
          </Fieldset>
        </Section>

        {/* Safety & Victimization Section */}
        <Section>
          <SectionHeader
            icon={ShieldExclamationIcon}
            title="Safety & Victimization"
            description="Safety concerns and victimization details"
          />
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Safety Information</Label>
                <Description>Document any safety concerns, restrictions, or special considerations</Description>
                <Textarea
                  rows={3}
                  value={safetyInformation}
                  placeholder="Safety concerns, restrictions, etc."
                  onChange={(e) => setSafetyInformation(e.target.value)}
                />
              </Field>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field>
                  <Label>Primary Victimization Type</Label>
                  <Select
                    value={primaryVictimizationType}
                    onChange={(e) => setPrimaryVictimizationType(e.target.value as VictimizationType)}
                  >
                    <option value="">Select type...</option>
                    {Object.entries(VICTIMIZATION_TYPE_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field>
                  <Label>Offender Name</Label>
                  <Input
                    type="text"
                    value={offenderName}
                    placeholder="Name of offender"
                    onChange={(e) => setOffenderName(e.target.value)}
                  />
                </Field>
              </div>
              {primaryVictimizationType === "other" && (
                <Field>
                  <Label>Other Victimization Type</Label>
                  <Input
                    type="text"
                    value={primaryVictimizationOther}
                    placeholder="Specify the type"
                    onChange={(e) => setPrimaryVictimizationOther(e.target.value)}
                  />
                </Field>
              )}
            </FieldGroup>
          </Fieldset>
        </Section>

        {/* Counseling Section */}
        <Section>
          <SectionHeader
            icon={HeartIcon}
            title="Counseling & Therapeutic Services"
            description="Counseling status and therapeutic visit preferences"
          />
          <Fieldset>
            <FieldGroup>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900">
                  <SwitchField>
                    <Label>Currently in Counseling</Label>
                    <Switch
                      checked={inCounseling}
                      onChange={() => setInCounseling(!inCounseling)}
                    />
                  </SwitchField>
                </div>
                <div className="rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900">
                  <SwitchField>
                    <Label>Court Ordered Counseling</Label>
                    <Switch
                      checked={courtOrderedCounseling}
                      onChange={() => setCourtOrderedCounseling(!courtOrderedCounseling)}
                    />
                  </SwitchField>
                </div>
                <div className="rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900">
                  <SwitchField>
                    <Label>Interested in Therapeutic Visits</Label>
                    <Switch
                      checked={interestedInTherapeuticVisits}
                      onChange={() => setInterestedInTherapeuticVisits(!interestedInTherapeuticVisits)}
                    />
                  </SwitchField>
                </div>
              </div>
            </FieldGroup>
          </Fieldset>
        </Section>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={clearForm}>
          Cancel
        </Button>
        <Button disabled={isUploading} onClick={handleSubmit}>
          {isUploading ? "Saving..." : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditCaseDialog;
